(() => {
  const activateMedia = (root, mediaId, direction = 0) => {
    if (!mediaId) return;
    const mediaItems = [...root.querySelectorAll('[data-product-media]')];
    const target = mediaItems.find((media) => String(media.dataset.mediaId) === String(mediaId));
    const current = mediaItems.find((media) => media.classList.contains('is-active'));
    if (!target) return;

    window.clearTimeout(root._mediaTransitionTimer);
    mediaItems.forEach((media) => {
      media.classList.remove('is-active', 'is-entering-next', 'is-entering-previous', 'is-leaving-next', 'is-leaving-previous');
      media.hidden = true;
    });

    if (direction && current && current !== target) {
      current.hidden = false;
      current.classList.add(direction > 0 ? 'is-leaving-next' : 'is-leaving-previous');
      target.hidden = false;
      target.classList.add('is-active', direction > 0 ? 'is-entering-next' : 'is-entering-previous');
      root._mediaTransitionTimer = window.setTimeout(() => {
        current.hidden = true;
        current.classList.remove('is-leaving-next', 'is-leaving-previous');
        target.classList.remove('is-entering-next', 'is-entering-previous');
      }, 420);
    } else {
      target.hidden = false;
      target.classList.add('is-active');
    }

    root.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
      const active = String(thumb.dataset.mediaTarget) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
    });
  };

  const initGallery = (root) => {
    const thumbs = [...root.querySelectorAll('[data-product-thumb]')];
    const stage = root.querySelector('[data-product-stage]');
    const mediaIds = [...root.querySelectorAll('[data-product-media]')].map((media) => media.dataset.mediaId).filter(Boolean);
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const currentIndex = mediaIds.indexOf(root.querySelector('[data-product-media].is-active')?.dataset.mediaId);
        const targetIndex = mediaIds.indexOf(thumb.dataset.mediaTarget);
        const direction = currentIndex >= 0 && targetIndex >= 0 && targetIndex !== currentIndex ? (targetIndex > currentIndex ? 1 : -1) : 0;
        activateMedia(root, thumb.dataset.mediaTarget, direction);
      });
    });
    if (!stage || mediaIds.length < 2) return;

    let swipeStart = null;
    const pointFromEvent = (event) => event.touches?.[0] || event.changedTouches?.[0] || event;
    const startSwipe = (event) => {
      if (!window.matchMedia('(max-width: 639px)').matches) return;
      if (event.type === 'mousedown' && event.button !== 0) return;
      if (event.target.closest('button, a, input, select, textarea')) return;
      const point = pointFromEvent(event);
      swipeStart = { x: point.clientX, y: point.clientY };
      stage.setPointerCapture?.(event.pointerId);
    };
    const finishSwipe = (event) => {
      if (!swipeStart) return;
      const point = pointFromEvent(event);
      const deltaX = point.clientX - swipeStart.x;
      const deltaY = point.clientY - swipeStart.y;
      swipeStart = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      const activeId = root.querySelector('[data-product-media].is-active')?.dataset.mediaId || mediaIds[0];
      const activeIndex = mediaIds.indexOf(activeId);
      const nextIndex = Math.min(mediaIds.length - 1, Math.max(0, activeIndex + (deltaX < 0 ? 1 : -1)));
      if (nextIndex !== activeIndex) activateMedia(root, mediaIds[nextIndex], deltaX < 0 ? 1 : -1);
    };
    stage.addEventListener('pointerdown', startSwipe);
    stage.addEventListener('pointerup', finishSwipe);
    stage.addEventListener('mousedown', startSwipe);
    stage.addEventListener('mouseup', finishSwipe);
    stage.addEventListener('touchstart', startSwipe, { passive: true });
    stage.addEventListener('touchend', finishSwipe, { passive: true });
    stage.addEventListener('pointercancel', () => { swipeStart = null; });
    stage.addEventListener('touchcancel', () => { swipeStart = null; });
  };

  const initPersonalization = (root) => {
    const button = root.querySelector('[data-personalize-toggle]');
    const panel = root.querySelector('[data-personalize-panel]');
    const input = root.querySelector('[data-personalize-input]');
    if (!button || !panel || !input) return;

    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(expanded));
      panel.hidden = !expanded;
      input.disabled = !expanded;
      if (expanded) input.focus();
      else input.value = '';
    });
  };

  const initDeliveryWindow = (root) => {
    const output = root.querySelector('[data-delivery-window]');
    if (!output) return;

    const addBusinessDays = (startDate, amount) => {
      const date = new Date(startDate);
      let remaining = amount;
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) remaining -= 1;
      }
      return date;
    };

    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL', {
      weekday: 'long', day: 'numeric', month: 'long'
    });
    const minimum = addBusinessDays(new Date(), Number(output.dataset.minDays) || 2);
    const maximum = addBusinessDays(new Date(), Number(output.dataset.maxDays) || 5);
    output.textContent = `${formatter.format(minimum)} – ${formatter.format(maximum)}`;
  };

  const initVariants = (root) => {
    const variantData = root.querySelector('[data-product-variants]');
    const variantId = root.querySelector('[data-variant-id]');
    const optionInputs = [...root.querySelectorAll('[data-product-option]')];
    if (!variantData || !variantId || optionInputs.length === 0) return;

    let variants = [];
    try {
      variants = JSON.parse(variantData.textContent);
    } catch {
      return;
    }

    const price = root.querySelector('.jq-product__price[data-product-price]');
    const comparePrice = root.querySelector('[data-product-compare-price]');
    const submit = root.querySelector('[data-product-submit]');
    const submitLabel = root.querySelector('[data-product-submit-label]');
    const wishlistToggle = root.querySelector('[data-wishlist-toggle]');
    const framePrices = {
      '21x30': { price: 11.05, compare: 13 },
      '30x40': { price: 20, compare: 30 },
      '50x70': { price: 38.21, compare: 44.95 },
      '70x100': { price: 52.66, compare: 61.95 }
    };
    const matPrices = {
      '21x30': { 3: { price: 2, compare: 4 }, 6: { price: 4, compare: 4 } },
      '30x40': { 3: { price: 6, compare: 9 }, 6: { price: 9, compare: 9 } },
      '50x70': { 3: { price: 11, compare: 25 }, 6: { price: 25, compare: 25 } },
      '70x100': { 3: { price: 26, compare: 57 }, 6: { price: 57, compare: 57 } }
    };
    const currency = root.dataset.productCurrency || 'EUR';
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { style: 'currency', currency });
    const formatMoney = (amount) => formatter.format(Number(amount) || 0);
    const sizeKey = (value) => {
      const numbers = String(value || '').match(/\d+/g)?.map(Number).sort((a, b) => a - b) || [];
      if (numbers.length !== 2) return null;
      const key = numbers.join('x');
      return framePrices[key] ? key : null;
    };
    const selectedFrame = () => root.querySelector('[data-frame-key]:checked');
    const selectedMat = () => root.querySelector('[data-mat-size]:checked');
    const updateAddonLabels = (key) => {
      root.querySelectorAll('[data-frame-key]').forEach((input) => {
        const delta = input.dataset.frameKey === 'none' ? null : framePrices[key];
        const holder = input.closest('.jq-frame-swatch');
        const current = holder?.querySelector('[data-frame-price]');
        const compare = holder?.querySelector('[data-frame-compare-price]');
        if (current) current.textContent = delta ? `+${formatMoney(delta.price)}` : '';
        if (compare) compare.textContent = '';
      });
      root.querySelectorAll('[data-mat-size]').forEach((input) => {
        const delta = input.dataset.matSize === '0' ? null : matPrices[key]?.[input.dataset.matSize];
        const holder = input.closest('label');
        const current = holder?.querySelector('[data-mat-price]');
        const compare = holder?.querySelector('[data-mat-compare-price]');
        if (current) current.textContent = delta ? `+${formatMoney(delta.price)}` : '';
        if (compare) compare.textContent = '';
      });
    };

    const selectedOptions = () => {
      const positions = [...new Set(optionInputs.map((input) => Number(input.dataset.optionPosition)).filter(Number.isFinite))].sort((a, b) => a - b);
      if (positions.length) {
        return positions.map((position) => root.querySelector(`[data-product-option][data-option-position="${position}"]:checked`)?.value || '');
      }
      return [...root.querySelectorAll('[data-option-group]')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((group) => group.querySelector('[data-product-option]:checked')?.value || '');
    };

    const updateVariant = () => {
      const selected = selectedOptions();
      const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
      const available = Boolean(variant?.available);

      if (variant) {
        const key = sizeKey(variant.options.find((option) => sizeKey(option)) || selected[0]);
        const frameDelta = selectedFrame()?.dataset.frameKey === 'none' ? null : framePrices[key];
        const matDelta = matPrices[key]?.[selectedMat()?.dataset.matSize] || null;
        // Shopify Liquid serializes variant prices as integer cents; add-on tables use euros.
        const basePrice = Number(variant.priceAmount ?? 0) / 100;
        const baseComparePrice = Number(variant.compareAtPriceAmount ?? variant.priceAmount ?? 0) / 100;
        const hasPricedAddonVariant = variant.options.length >= 3 && variant.options[1] && variant.options[2];
        const total = hasPricedAddonVariant ? basePrice : basePrice + (frameDelta?.price || 0) + (matDelta?.price || 0);
        const compareTotal = hasPricedAddonVariant ? baseComparePrice : baseComparePrice + (frameDelta?.compare || 0) + (matDelta?.compare || 0);
        variantId.value = variant.id;
        if (price) price.textContent = formatMoney(total);
        if (wishlistToggle) wishlistToggle.dataset.productPrice = formatMoney(total);
        if (comparePrice) {
          comparePrice.textContent = compareTotal > total ? formatMoney(compareTotal) : '';
          comparePrice.hidden = compareTotal <= total;
        }
        updateAddonLabels(key);
        if (variant.featuredMediaId) activateMedia(root, variant.featuredMediaId);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      if (submit) submit.disabled = !available;
      if (submitLabel && submit) submitLabel.textContent = available ? submit.dataset.addLabel : submit.dataset.soldOutLabel;
    };

    optionInputs.forEach((input) => input.addEventListener('change', updateVariant));
    root.querySelectorAll('[data-frame-key],[data-mat-size]').forEach((input) => input.addEventListener('change', updateVariant));
    updateVariant();
  };

  const initQuantity = (root) => {
    const input = root.querySelector('[data-product-quantity]');
    const decrease = root.querySelector('[data-quantity-decrease]');
    const increase = root.querySelector('[data-quantity-increase]');
    if (!input || !decrease || !increase) return;

    const normalize = (value = input.value) => {
      const quantity = Math.max(1, Math.floor(Number(value) || 1));
      input.value = String(quantity);
      decrease.disabled = quantity <= 1;
      return quantity;
    };

    decrease.addEventListener('click', () => normalize(normalize() - 1));
    increase.addEventListener('click', () => normalize(normalize() + 1));
    input.addEventListener('input', () => {
      if (input.value !== '') normalize();
    });
    input.addEventListener('change', () => normalize());
    input.addEventListener('blur', () => normalize());
    normalize();
  };

  const initProduct = (root) => {
    initGallery(root);
    initPersonalization(root);
    initDeliveryWindow(root);
    initVariants(root);
    initQuantity(root);
  };

  const boot = () => document.querySelectorAll('[data-product-root]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
